import type { NotificationHandler } from './notification-handler'
import type { ChannelResponse, NotificationChannelInfo } from '../notification-channel/models'
import { ShellWebSocket } from './shell-websocket'
import { isChannelResponse } from '../notification-channel/channel'
import { NotificationHandlerEventType } from './notification-handler-event'
import {
  CHANNEL_REFRESH_PERCENTAGE,
  PING_INTERVAL_MS,
  PONG_TIMEOUT_INTERVAL_MS,
  RESUME_CONNECTION_DELAY_IN_MS,
} from './model'
import { getShellLogger } from '../../common/logger'
import { getRandomInt } from '../../core/helpers/math'
import { Interval, Timer } from '../notification-channel/helpers'
import { isUserOnline } from '../../common/helpers'
import type { NotificationChannelHandlerParams } from './notification-handler-factory'
import type { NodeEventEmitter } from '../namespaces'

export const KICKED_BY_NEWER_CONNECTION = 'Kicked by newer connection'
export const GOING_AWAY_CLOSE_CODE = 1001
/**
 * Tests cases for the websocket reconnection
 *
 * 1.
 * Close the wifi for few seconds and toggle it back.
 * The websocket should try to ping with the last websocket instance and
 * if there's no pong back, it should create a new instance.
 *
 * 2.
 * Close the wifi just before the channel-lifetime should be updated.
 * Toggle back the wifi, a new websocket instance should be created.
 *
 * 3.
 * Put the computer to sleep, wait few seconds before waking it up.
 * It should try to create a new channel until the internet come back (it chould take few times).
 * Then a new websocket instance should be created.
 */
export class WebSocketNotificationHandler extends ShellWebSocket implements NotificationHandler {
  public channelInfo: NotificationChannelInfo

  private emitter: NodeEventEmitter
  private pongTimeout: Timer | null = null
  private pingInterval: Interval | null = null
  private connectionTimeout: Timer | null = null
  private verifyOnlineStatusInterval: Interval | null = null

  private connectionAttemptsCount = 0
  private channelStartTimestamp = 0

  constructor(params: NotificationChannelHandlerParams) {
    super()
    this.channelInfo = params.channelInfo
    this.emitter = params.emitter
    this.updateChannelInfo({})
  }

  get isConnected() {
    return this.isOpen()
  }

  public connect(): void {
    this.connectionAttemptsCount = 0
    this.keepAlive()
  }

  public updateChannelInfo(channelInfo: Partial<NotificationChannelInfo>) {
    this.channelInfo = {
      ...this.channelInfo,
      ...channelInfo,
    }
    this.channelStartTimestamp = new Date().valueOf()
  }

  public delete(): void {
    this.pause()
    this.disconnect()
    this.connectionTimeout?.stop()
  }

  public pause(): void {
    this.pongTimeout?.pause()
    this.pingInterval?.pause()
  }

  public resume(): void {
    this.pongTimeout?.stop()
    this.pingInterval?.reset()
  }

  protected onOpen(event: Event): void {
    this.connectionAttemptsCount = 0
    this.connectionTimeout?.stop()
    this.startInternalPing()
    this.emitter.emit(NotificationHandlerEventType.OPEN, event)
  }

  protected onClose(event: CloseEvent): void {
    switch (true) {
      case this.isTerminated:
      case this.isOpen():
      case this.isConnecting():
      case event?.reason === KICKED_BY_NEWER_CONNECTION:
      case event?.code === GOING_AWAY_CLOSE_CODE:
        return
    }

    this.pingInterval?.stop()
    this.watchForBackOnline()
    this.emitter.emit(NotificationHandlerEventType.CLOSE, event)
  }

  protected onError(event: Event): void {
    this.emitter.emit(NotificationHandlerEventType.ERROR, event)
  }

  protected onMessage(event: MessageEvent<string>): void {
    let message: ChannelResponse | undefined
    try {
      message = JSON.parse(event.data)
    } catch (error) {
      getShellLogger().error('WebSocket: Invalid JSON', error)
    }

    if (!message) {
      return
    }

    if (isChannelResponse(message) && this.isPongMessage(message.correlationId)) {
      this.handlePongMessage(message.correlationId)
      return
    }

    if (message.code < 200 || message.code > 299) {
      getShellLogger().error(`WebSocket:Message contains ErrorCode ${message.code}`, message)
    } else {
      this.emitter.emit(NotificationHandlerEventType.MESSAGE, message)
    }
  }

  private watchForBackOnline() {
    if (!this.verifyOnlineStatusInterval) {
      this.verifyOnlineStatusInterval = new Interval(() => {
        if (isUserOnline()) {
          this.verifyOnlineStatusInterval?.stop()
          this.verifyOnlineStatusInterval = null
          this.emitRestartRequiredEvent()
        }
      }, RESUME_CONNECTION_DELAY_IN_MS)
    }
  }

  private startInternalPing(): void {
    this.pingInterval?.stop()
    this.pingInterval = new Interval(() => {
      this.internalPing()
    }, PING_INTERVAL_MS)
  }

  private internalPing(): void {
    if (this.isChannelLifeTimeOutdated() || this.isClosed()) {
      this.emitRestartRequiredEvent()
    } else {
      this.ping()
      this.checkForChannelLifeTimeUpdate()

      this.pongTimeout?.stop()
      this.pongTimeout = new Timer(() => {
        this.handlePongMessageTimeout()
      }, PONG_TIMEOUT_INTERVAL_MS)
    }
  }

  protected handlePongMessageTimeout() {
    this.handlePongMessage()
    getShellLogger().error('WebSocket:Pong timeout')
  }

  protected handlePongMessage(correlationId?: number) {
    this.pongTimeout?.stop()
    super.handlePongMessage(correlationId)
  }

  private checkForChannelLifeTimeUpdate() {
    const timeSpent = this.getTimeSpentSinceLastChannelLifetimeUpdate()

    if (this.channelInfo.channelLifetime * CHANNEL_REFRESH_PERCENTAGE < timeSpent) {
      this.emitUpdateChannelLifetimeEvent()
    }
  }

  private isChannelLifeTimeOutdated() {
    const timeSpent = this.getTimeSpentSinceLastChannelLifetimeUpdate()

    return timeSpent > this.channelInfo.channelLifetime
  }

  private keepAlive(): void {
    this.connectionAttemptsCount++

    if (this.connectionAttemptsCount > 3) {
      this.emitRestartRequiredEvent()
    } else {
      this.tryConnecting()
    }
  }

  private tryConnecting(): void {
    if (this.channelInfo.channelData) {
      try {
        this.internalConnect(this.channelInfo.channelData.channelURL)
      } catch (error) {
        getShellLogger().error('WebSocket:Connection failed', error)
      }
    } else {
      getShellLogger().error('WebSocket:ChannelData required')
    }

    const offsetInSec = this.connectionAttemptsCount ** 2
    const min = 2 + offsetInSec
    const max = 5 + offsetInSec
    const delayInMilliseconds = getRandomInt(min, max) * 1000

    this.connectionTimeout?.stop()
    this.connectionTimeout = new Timer(() => {
      this.keepAlive()
    }, delayInMilliseconds)
  }

  private emitRestartRequiredEvent(): void {
    this.emitter.emit(NotificationHandlerEventType.RESTART_REQUIRED)
  }

  private emitUpdateChannelLifetimeEvent(): void {
    this.emitter.emit(NotificationHandlerEventType.UPDATE_CHANNEL_LIFETIME)
  }

  private getTimeSpentSinceLastChannelLifetimeUpdate(): number {
    return Math.floor((new Date().valueOf() - this.channelStartTimestamp) / 1000)
  }
}

export const WebSocketNotificationHandlerFactory = (params: NotificationChannelHandlerParams) =>
  new WebSocketNotificationHandler(params)
